import React, {Component} from "react"
import { MathKatex2 } from "../../../../components/MathKatex"
import { SimpleInfoMap } from "../../../../services/practice-test-scoring/practice-test-summary"
import ComputerTestACT from "./ComputerTestACT"
import "./AnswersGroup.css"

interface Option {
    label: string
    value: string
}

interface Props {
    questionNumber: number
    options: Option[]
    selectedAnswer: string
    eliminatedInfo?: SimpleInfoMap
    reverseMaskedInfo?: SimpleInfoMap
    onSelect: (value: string) => void
    onEliminate: (choice: string, eliminated: boolean) => void
    onReverseMask: (choice: string, reverseMasked: boolean) => void
}

interface State {
    selectedValue: string
}

/**
 * This component emulates the ACT Answer group with multiple-choice
 */
export default class AnswersGroup extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            selectedValue: props.selectedAnswer,
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
        if (prevProps.selectedAnswer !== this.props.selectedAnswer) {
            this.setState({selectedValue: this.props.selectedAnswer})
        }
    }

    handleSelect = (value: string) => {
        this.setState({ selectedValue: value })
        this.props.onSelect(value)
    }

    handleEliminate = (choice, crossed) => {
        this.props.onEliminate(choice, crossed)
    }

    handleReverseMask = (choice, reverseMasked) => {
        this.props.onReverseMask(choice, reverseMasked)
    }

    render() {
        let {questionNumber, selectedAnswer, eliminatedInfo, reverseMaskedInfo } = this.props

        return (
            <div className="answers-group">
                { this.props.options.map((option, index) => {
                    let isSelected = this.state.selectedValue === option.value

                    return (
                        <Answer
                            key={`answer-${index}`}
                            value={option.value}
                            label={option.label}
                            selected={isSelected}
                            eliminatedInfo={eliminatedInfo}
                            reverseMaskedInfo={reverseMaskedInfo}
                            onSelect={this.handleSelect}
                            onEliminate={this.handleEliminate}
                            onReverseMask={this.handleReverseMask}
                        />
                    )})
                }
            </div>
        )
    }
}


//--- Helpers ---
interface IAnswerProps {
    value: string
    label: string
    selected: boolean
    eliminatedInfo: any
    reverseMaskedInfo: any
    onSelect: (value) => void
    onEliminate: (choice: string, eliminated: boolean) => void
    onReverseMask: (choice: string, reverseMasked: boolean) => void
}

interface IAnswerState {
    selected: boolean
    eliminated: boolean
    reverseMasked: boolean
}

/**
 * This component will be emulating the ACT answer behavior.
 */
class Answer extends Component<IAnswerProps, IAnswerState> {
    state = {
        selected: false,
        eliminated: false,
        reverseMasked: false,
    }

    componentDidMount(): void {
        this.initialize()
    }

     componentDidUpdate(prevProps: Readonly<IAnswerProps>, prevState: Readonly<IAnswerState>, snapshot?: any): void {
        if (prevProps.label !== this.props.label || prevProps.value !== this.props.value || prevProps.selected !== this.props.selected) {
            this.initialize()
        }
    }

    initialize = () => {
        let { eliminatedInfo, reverseMaskedInfo } = this.props
        let eliminated = eliminatedInfo ? eliminatedInfo[this.props.value]: false
        let reverseMasked = reverseMaskedInfo ? reverseMaskedInfo[this.props.value]: false
        this.setState({eliminated, reverseMasked})
    }

    toggleEliminate = () => {
        let {eliminated} = this.state
        eliminated = !eliminated
        this.setState({eliminated})
        this.props.onEliminate(this.props.value, eliminated)
    }

    toggleReverseMasking = () => {
        let {reverseMasked} = this.state
        reverseMasked = !reverseMasked
        this.setState({reverseMasked})
        this.props.onReverseMask(this.props.value, reverseMasked)
    }

    handleSelect = () => {
        let {value, onSelect} = this.props
        this.setState({eliminated: false})
        onSelect(value)
    }

    render() {
        let {value, label, selected} = this.props
        let {eliminated, reverseMasked} = this.state
        let {answerEliminator, answerMasking} = ComputerTestACT.TOOLS_MAP

        // Note that it is reverse masked, i.e. initially all answers will be masked!
        let masked = !reverseMasked && answerMasking.selected
        eliminated = eliminated && answerEliminator.selected

        return (
            <div key={value} className="answer-container">
                 <div className={`answer-section `}>
                    <button key={value} onClick={this.handleSelect} style={{width: "100%"}}>
                        <div className={`mc-option ${selected ? "selected" : ""} ${eliminated ? "faded-option": ""} ${masked ? "masked": ""}`}>
                            <span className={`mc-circle`}>{value}</span>
                            <span className={`mc-label ${masked ? "masked": ""}`}><MathKatex2 mixedContent={label} /></span>
                        </div>
                    </button>
                    <div className={`${eliminated ? "crossed-out-x": ""}`} />
                </div>
                { answerEliminator.selected  && 
                    <div className="answer-eliminate-section">
                        <button
                            onClick={this.toggleEliminate}
                            aria-label={eliminated ? "Uneliminate Answer" : "Eliminate Answer"}
                            className={`eact-btn alt eact-btn-eliminator ${eliminated ? "toggled" : ""}`}
                        >
                            <img src="/assets/images/icons/v2/ico-x-elim.svg" width="14" height="14" alt="" className=""/>
                            <span className="sr-only">Eliminate</span>
                        </button>
                    </div>
                }
                { answerMasking.selected && 
                    <div className="answer-mask-section">
                        <button 
                            onClick={this.toggleReverseMasking} 
                            aria-label={masked ? "Unmask Answer" : "Mask Answer"}
                            className={`eact-btn alt eact-btn-masking ${masked ? "toggled" : ""}`}>
                           <img
                                src={`/assets/images/icons/v2/${masked ? "ico-eye-mask" : "ico-eye"}.svg`}
                                width="14"
                                height="14"
                                alt=""
                            />
                            <span className="sr-only">Mask</span>
                        </button>
                    </div>
                }
            </div>
        )
    }
}
